/** @format */

/* eslint @typescript-eslint/non-nullable-type-assertion-style: "warn", @typescript-eslint/no-unsafe-enum-comparison: "warn" */
import { TFunction } from 'i18next';

import { getMarketFromCode } from '@toucan/common/services/market/helpers';
import { convertModelNewPriceToPrice } from '@toucan/common/services/model/helpers';

import {
    decimalToCent,
    formatAsCurrency,
    formatAsDecimal,
} from '@toucan/client/global/currency';
import {
    AvailableMarket,
    Market,
    MarketCode,
    MarketSlug,
} from '@toucan/client/global/market';
import { Model } from '@toucan/client/global/model';
import {
    AttributeKey,
    PriceModifier,
    Product,
    ProductConditionValue,
    ProductFormattedAttribute,
    ProductState,
    ProductSummaryAttribute,
    SavedProduct,
} from '@toucan/client/global/product';

export const USAGE_ATTRIBUTES = [
    AttributeKey.SHUTTER_COUNT,
    AttributeKey.BATTERY_CHARGE_COUNT,
    AttributeKey.HOUR_COUNT,
];

export const fallbackImagePath = '/static/images/camera-placeholder.png';

export const getProductConditions = (t: TFunction) => {
    return [
        {
            name: t('Brand new'),
            value: ProductConditionValue.NEW,
        },
        {
            name: t('Like new'),
            value: ProductConditionValue.LIKE_NEW,
        },
        {
            name: t('Excellent'),
            value: ProductConditionValue.EXCELLENT,
        },
        {
            name: t('Good'),
            value: ProductConditionValue.GOOD,
        },
        {
            name: t('Well used'),
            value: ProductConditionValue.WELL_USED,
        },
        {
            name: t('Heavily used'),
            value: ProductConditionValue.HEAVILY_USED,
        },
        {
            name: t('Spares and repairs'),
            value: ProductConditionValue.FAULTY,
        },
    ];
};

type GetProductConditionDescriptionArgs = {
    locale: string;
    t: TFunction;
    batteryChargeCount?: number;
    hourCount?: number;
    shutterCount?: number;
};

type getProductAttributesListArgs = {
    locale: string;
    t: TFunction;
    batteryChargeCount?: number;
    hourCount?: number;
    shutterCount?: number;
    condition: string;
};

/**
 * Converts a product condition string from the api to an enum
 */
export function productConditionStringToEnum(
    condition: string,
    allowUnknownCondition: boolean = false
): ProductConditionValue {
    const preparedCondition = condition
        .replace(/[-_]/g, ' ')
        .replace(/[/]/g, '')
        .toLowerCase()
        .trim();
    /**
     * Translations for product conditions are in the following order:
     * EN
     * FR
     * DE
     */
    switch (preparedCondition) {
        case 'new':
        case 'brand new':
        case 'neuf':
        case 'tout neuf':
        case 'neu':
        case 'brandneu':
        case 'nieuw':
        case 'gloednieuw':
        case 'nuovo':
        case 'nuevo':
        case 'como recién estrenado':
        case 'como recien estrenado':
        case 'nova':
            return ProductConditionValue.NEW;

        case 'like new':
        case 'comme neuf':
        case 'wie neu':
        case 'neuwertig':
        case 'als nieuw':
        case 'come nuovo':
        case 'como nuevo':
        case 'como nova':
            return ProductConditionValue.LIKE_NEW;

        // FR translation is the same as EN
        case 'excellent':
        case 'ausgezeichnet':
        case 'hervorragend':
        case 'uitstekend':
        case 'eccellente':
        case 'excelente':
            return ProductConditionValue.EXCELLENT;

        case 'good':
        case 'bon':
        case 'gut':
        case 'goed':
        case 'buono':
        case 'bueno':
        case 'boa':
            return ProductConditionValue.GOOD;

        case 'well used':
        case 'médiocre':
        case 'mediocre':
        case 'usé':
        case 'use':
        case 'gut gebraucht':
        case 'normale gebrauchsspuren':
        case 'veel gebruikt':
        case 'molto usato':
        case 'muy usado':
        case 'muito usada':
            return ProductConditionValue.WELL_USED;

        case 'heavily used':
        case 'très usé':
        case 'tres use':
        case 'schwer gebraucht':
        case 'schwere gebrauchsspuren':
        case 'intensief gebruikt':
        case 'fortemente usato':
        case 'uso intensivo':
        case 'aceitável':
        case 'aceitavel':
            return ProductConditionValue.HEAVILY_USED;

        case 'faulty':
        case 'spares and repairs':
        case 'sr':
        case 'défectueux':
        case 'defectueux':
        case "hors d'usage":
        case 'hors dusage':
        case 'fehlerhaft':
        case 'defekt':
        case 'onderdelen en reparaties':
        case 'ricambi e riparazioni':
        case 'dañado':
        case 'danado':
        case 'repuestos y reparaciones':
        case 'peças e reparações':
        case 'pecas e reparacoes':
            return ProductConditionValue.FAULTY;

        default:
            if (allowUnknownCondition && preparedCondition === 'unknown') {
                return ProductConditionValue.UNKNOWN;
            }
            throw new Error(`No enum for ${condition}`);
    }
}

/**
 * Converts a product condition enum to text to be displayed
 */
export function productConditionEnumToText(
    t: TFunction,
    condition: ProductConditionValue,
    allowUnknownCondition: boolean = false
): string {
    const preparedCondition = condition
        .replace(/[-]/g, '_')
        .replace(/[/]/g, '')
        .trim();

    switch (preparedCondition) {
        case ProductConditionValue.NEW:
            return t('Brand new');

        case ProductConditionValue.LIKE_NEW:
            return t('Like new');

        case ProductConditionValue.EXCELLENT:
            return t('Excellent');

        case ProductConditionValue.GOOD:
            return t('Good');

        case ProductConditionValue.WELL_USED:
            return t('Well used');

        case ProductConditionValue.HEAVILY_USED:
            return t('Heavily used');

        case ProductConditionValue.SR:
        case ProductConditionValue.FAULTY:
            return t('Spares and repairs');

        default:
            if (
                allowUnknownCondition &&
                preparedCondition === ProductConditionValue.UNKNOWN
            ) {
                return t('Unknown');
            }
            throw new Error(
                `Cannot convert ${condition} (${preparedCondition}) to text`
            );
    }
}

export function productConditionEnumToLegacyEnum(
    condition: ProductConditionValue
): ProductConditionValue {
    switch (condition) {
        case ProductConditionValue.LIKE_NEW:
            return ProductConditionValue['LIKE-NEW'];
        case ProductConditionValue.WELL_USED:
            return ProductConditionValue['WELL-USED'];
        case ProductConditionValue.HEAVILY_USED:
            return ProductConditionValue['HEAVILY-USED'];
        case ProductConditionValue.FAULTY:
            return ProductConditionValue.SR;
        default:
            return condition;
    }
}

/**
 * Takes a saved product and converts it to what we expect
 */
export function transformSavedProduct(
    product: SavedProduct,
    activeMarket: Market | AvailableMarket,
    availableMarkets: AvailableMarket[],
    t: TFunction
): SavedProduct {
    const formatPrice = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    const productMarket = getMarketFromCode(product.market, availableMarkets);

    const marketCode =
        productMarket?.code ?? (product.market.toUpperCase() as MarketCode);

    const productSlug = t('product', { ns: 'urls' });

    return {
        name: product.name,
        id: product.id,
        modelId: product.modelId,
        image: product.images[0]?.imageUrl,
        sku: product.sku,
        description: product.description,
        price: formatPrice(parseFloat(product.listPrice)),
        value: decimalToCent(parseFloat(product.listPrice)),
        listPrice: product.listPrice,
        condition: product.condition,
        linkHref: `/${activeMarket.slug}/${productSlug}/${product.modelSlug}/sku-${product.sku}`,
        modelSlug: product.modelSlug,
        market: marketCode,
        images: product.images,
        state:
            product.state === ProductState.ONLINE
                ? ProductState.ONLINE
                : ProductState.OFFLINE,
    };
}

/**
 * Gets the product's condition description
 */
export function getProductConditionDescription({
    t,
    locale,
    batteryChargeCount,
    hourCount,
    shutterCount,
}: GetProductConditionDescriptionArgs) {
    if (batteryChargeCount) {
        return t('condition with a battery charge count of {{value}} charges', {
            value: formatAsDecimal(locale, batteryChargeCount),
        });
    }

    if (hourCount) {
        return t('condition with an hour count of {{value}} hours', {
            value: formatAsDecimal(locale, hourCount),
        });
    }

    if (shutterCount) {
        return t('condition with a shutter count of {{value}}', {
            value: formatAsDecimal(locale, shutterCount),
        });
    }

    return t('condition');
}

/**
 * Currently we'll just be getting an array of modifier names from the back end,
 * in future we'll receive info about whether it's present or not so we'll need to refactor this
 */
export const formatPriceModifiers = (priceModifiers: string[]) => {
    const formattedPriceModifiers: PriceModifier[] =
        priceModifiers?.map((priceModifier, index) => {
            return {
                id: index + 1,
                name: priceModifier,
                core: true,
                essential: false,
                present: true,
            };
        }) ?? [];
    return formattedPriceModifiers;
};

/**
 * Gets a list of attributes to display
 */
export function getProductAttributes({
    t,
    locale,
    batteryChargeCount,
    hourCount,
    shutterCount,
}: getProductAttributesListArgs): ProductFormattedAttribute[] {
    const attributes: ProductSummaryAttribute[] = [];

    if (batteryChargeCount !== undefined) {
        attributes.push({
            // @ts-ignore - Touch and Change, TFunction is not compatible with AttributeKey
            name: t('Charges'),
            content: formatAsDecimal(locale, batteryChargeCount),
        });
    }

    if (hourCount !== undefined) {
        attributes.push({
            // @ts-ignore - Touch and Change, TFunction is not compatible with AttributeKey
            name: t('Hours'),
            content: formatAsDecimal(locale, hourCount),
        });
    }

    if (shutterCount !== undefined) {
        attributes.push({
            // @ts-ignore - Touch and Change, TFunction is not compatible with AttributeKey
            name: t('Shutter count'),
            content: formatAsDecimal(locale, shutterCount),
        });
    }

    return attributes;
}

/**
 * Gets the product's includes list
 * @param t Translation fn
 * @param priceModifiers List of product_price_modifiers
 * @param modelAvailableNew If the model is new
 */
export function getProductIncludesList(
    t: TFunction,
    priceModifiers: string[],
    modelAvailableNew: boolean,
    condition: ProductConditionValue
) {
    const includesList = [...priceModifiers];

    if (!modelAvailableNew && !isConditionSparesAndRepairs(condition)) {
        includesList.unshift(t('Includes twelve month warranty'));
    }

    return includesList.join(', ');
}

/**
 * Maps a model to a new product
 *
 * @param {Model} model - The model to convert
 * @param {Market} market - The market for the new product
 */
export function convertModelToNewProduct(
    model: Model,
    market: Market,
    t: TFunction
): Product {
    const formatPrice = formatAsCurrency(market.iso, market.currency);

    const productSlug = t('product', { ns: 'urls' });

    return {
        name: model.name,
        modelId: model.id,
        image: model.images[0]?.imageUrl ?? '',
        sku: model.newSku.toString(),
        description: model.name ?? '',
        price: formatPrice(convertModelNewPriceToPrice(model.newPrice)),
        condition: ProductConditionValue.NEW,
        linkHref: `/${market.slug}/${productSlug}/${model.urlSegment}`,
    };
}

export const sparesAndRepairsConditionValues = [
    ProductConditionValue.FAULTY,
    ProductConditionValue.SR,
    ProductConditionValue['S/R'],
];

export function isConditionSparesAndRepairs(
    condition: ProductConditionValue | undefined
): boolean {
    return sparesAndRepairsConditionValues.includes(condition!);
}

export const getWarrantyLengthForMarket = (
    t: TFunction,
    market: Market,
    isNumeric: boolean = true
) => {
    const marketsWithSixMonthWarranties = [MarketSlug.EN_US];

    if (marketsWithSixMonthWarranties.includes(market.slug)) {
        return isNumeric ? '6' : t('six');
    }

    return isNumeric ? '12' : t('twelve');
};

/**
 * This plays no part in modifying the price of a product
 * But is returned alongside the price modifiers hence the type definition
 */
export const getWarrantyPriceModifier = ({
    t,
    condition,
    activeMarket,
}: {
    t: TFunction;
    condition: ProductConditionValue;
    activeMarket: Market;
}): PriceModifier | null => {
    if (isConditionSparesAndRepairs(condition)) {
        return null;
    }

    const warrantyString = t('Standard {{warrantyLength}} month warranty', {
        warrantyLength: getWarrantyLengthForMarket(t, activeMarket),
    });

    return {
        id: 0,
        name: warrantyString,
        core: true,
        essential: false,
        present: true,
    };
};

export const preparePriceModifiers = ({
    t,
    condition,
    activeMarket,
    priceModifiers,
}: {
    t: TFunction;
    condition: ProductConditionValue;
    activeMarket: Market;
    priceModifiers: PriceModifier[];
}) => {
    const sortedPriceModifiers = [...priceModifiers]
        .sort((a, b) => a.name.localeCompare(b.name))
        .reduce<Record<'included' | 'notIncluded', PriceModifier[]>>(
            (sorted, current) => {
                if (current.present) {
                    sorted.included.push(current);
                } else if (current.core) {
                    sorted.notIncluded.push(current);
                }

                return sorted;
            },
            {
                included: [],
                notIncluded: [],
            }
        );

    const warrantyPriceModifier = getWarrantyPriceModifier({
        t,
        activeMarket,
        condition,
    });

    // we display the standard warranty alongside included price modifiers
    if (warrantyPriceModifier) {
        sortedPriceModifiers.included.unshift(warrantyPriceModifier);
    }

    return sortedPriceModifiers;
};

export const isUsageAttribute = (attributeKey: AttributeKey) =>
    USAGE_ATTRIBUTES.includes(attributeKey);

export const getAttributeTestId = (attributeKey: AttributeKey) =>
    attributeKey.toLowerCase().replaceAll('_', '-');

export const getAttributeValue = (
    attributes: ProductSummaryAttribute[],
    attributeKey: AttributeKey
) => attributes.find((attr) => attr.name === attributeKey)?.content ?? null;
